// One card's view of the page's move controller (#6771): `MissionCard` provides it to its
// zones, so a job row, an epic row and a group header reach the machine, its blocker join and
// the page's controller without threading them through four components. Provided ONLY for an
// admin on this org's own machine (the card controls' gate, `./card-controls-context.ts`
// `on()`, owner rulings G21 and G23): a member, a viewer or another org's machine gets no
// grip, no Move button and no drop target. Outside a Fleet page there is no controller and so
// no handle.

import { createContext, useContext } from 'solid-js';
import type { ApiBlocker } from './blockers';
import type { UtilBox } from './fleetUtilization';
import { mayPress } from './move-drag';
import type { MoveSubject } from './move-items';
import type { MovePage } from './move-page';

export type CardMove = {
  readonly page: MovePage;
  readonly box: () => UtilBox;
  readonly byTask: () => ReadonlyMap<string, readonly ApiBlocker[]>;
  /** Writable now: an admin on an own-org machine (read reactively, a role settles late). */
  readonly on: () => boolean;
};

export const CardMoveContext = createContext<CardMove | null>(null);

/** This card's move handle when the person may move work off it, else null. */
export function useCardMove(): CardMove | null {
  return useContext(CardMoveContext) ?? null;
}

/** Why this card's grips are disabled now, translated, or null; tracked. */
export function gripWhy(m: CardMove): string | null {
  return m.page.blockedWhy(m.box().id);
}

/** What a press on a row may not start from: its controls (the row's own toggle excepted). */
const CONTROL =
  'button, select, input, textarea, a, details, [role="menu"], .move-menu, .mission-jobs__detail';
const ROW_TOGGLE = '.mission-jobs__row-toggle, .mission-jobs__group-head';

/**
 * A row's `pointerdown`: arm a drag when the press may start one (`mayPress`, `./move-drag.ts`):
 * a mouse from the row or its header but not its controls, touch and pen from the grip only,
 * never from a disabled grip. A mouse press off the grip keeps the page from selecting text.
 */
export function pressMove(m: CardMove, subject: MoveSubject, e: PointerEvent): void {
  const row = e.currentTarget;
  if (!(row instanceof HTMLElement)) return;
  const target = e.target instanceof Element ? e.target : null;
  const grip = target?.closest('[data-move-grip]') ?? null;
  const control = target?.closest(CONTROL) ?? null;
  const ok = mayPress({
    pointerType: e.pointerType,
    button: e.button,
    onGrip: grip !== null,
    // The row's control, wherever the press lands: a disabled move arms nothing.
    gripDisabled: m.page.blockedWhy(subject.sourceId) !== null,
    onControl: control !== null && !control.matches(ROW_TOGGLE),
    hasGrip: row.querySelector(`[data-move-grip="${subject.key}"]`) !== null,
  });
  if (!ok) return;
  if (grip === null) e.preventDefault();
  m.page.press({ subject, row, event: e });
}
